import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import clsx from 'clsx'
import { useAppForm, FormField, TextInput, SelectInput } from '@tandem/ui/form'
import $type from '@tandem/ui/type.css'
import Spinner from '@tandem/ui/Spinner'
import { useTRPC } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import CounterpartyModal from '../CounterpartyModal'
import { fmt, type Row } from '../types'
import $ from './-Counterparties.css'
import $layout from '@tandem/ui/layout.css'
import $anim from '@tandem/ui/anim.css'
import $table from '@tandem/ui/table.css'
import $badge from '@tandem/ui/badge.css'

export const Route = createFileRoute('/proexpert/')({
  component: CounterpartiesPage,
})

let SHORT: Record<string, string> = { sez: 'СЭЗ', iii: 'ИИИ', licrm: 'РПН', egrul: 'ЕГРЮЛ' }

type Sort = { key: string; dir: 'asc' | 'desc' }

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  class: cls,
  title,
}: {
  label: string
  sortKey: string
  sort: Sort
  onSort: (key: string) => void
  class?: string
  title?: string
}) {
  let active = sort.key === sortKey
  return (
    <th class={clsx(cls, $.sortable)} title={title} onClick={() => onSort(sortKey)}>
      {label}
      <span class={$.arrow}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</span>
    </th>
  )
}

function CounterpartiesPage() {
  let { company } = useAuth()
  let trpc = useTRPC()
  let enabled = company?.features?.includes('proexpert') ?? false

  let { data: meta = [] } = useQuery({ ...trpc.proexpert.registries.queryOptions(), enabled, staleTime: Infinity })
  let { data: rows = [], isFetching } = useQuery({ ...trpc.proexpert.counterparty.list.queryOptions(), enabled })
  let { data: status } = useQuery({ ...trpc.proexpert.status.queryOptions(), enabled, refetchInterval: 5000 })

  let form = useAppForm({
    defaultValues: {
      search: '',
      registry: '' as string | undefined,
      presence: 'has',
      changed: '' as string | undefined,
      risky: false,
    },
  })
  let search = useStore(form.store, (s) => s.values.search)
  let registry = useStore(form.store, (s) => s.values.registry)
  let presence = useStore(form.store, (s) => s.values.presence)
  let changed = useStore(form.store, (s) => s.values.changed)
  let risky = useStore(form.store, (s) => s.values.risky)

  let [selected, setSelected] = useState<string | null>(null)
  let [sort, setSort] = useState<Sort>({ key: 'name', dir: 'asc' })
  let toggleSort = (key: string) =>
    setSort((s) =>
      s.key === key ?
        { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'name' ? 'asc' : 'desc' },
    )

  let progress = status?.progress?.state === 'running' ? status.progress : null
  let syncing = progress?.phase === 'registries' ? progress.inn : null
  let columnCount = meta.length + 4

  let filtered = useMemo(() => {
    let q = search.trim().toLowerCase()
    let cutoff = changed ? Date.now() - Number(changed) * 86_400_000 : 0
    let matched = rows
      .filter((r) => !q || r.inn.includes(q) || (r.company_name ?? '').toLowerCase().includes(q))
      .filter((r) => !registry || ((r.registries[registry] ?? 0) > 0) === (presence === 'has'))
      .filter((r) => !cutoff || (r.last_diff_at != null && Date.parse(r.last_diff_at) >= cutoff))
      .filter((r) => !risky || r.risks.length > 0)

    let mult = sort.dir === 'asc' ? 1 : -1
    let name = (r: Row) => r.company_name ?? ''
    let ts = (r: Row) => Date.parse(r.synced_at ?? r.updated_at ?? '') || 0
    let primary = (a: Row, b: Row) => {
      if (sort.key === 'name') return name(a).localeCompare(name(b), 'ru')
      if (sort.key === 'synced') return ts(a) - ts(b)
      let reg = sort.key.slice(4) // 'reg:<label>'
      return (a.registries[reg] ?? 0) - (b.registries[reg] ?? 0)
    }
    // Tie-break on name so equal-valued rows (e.g. same registry count) stay alphabetical.
    return matched.sort((a, b) => primary(a, b) * mult || name(a).localeCompare(name(b), 'ru'))
  }, [rows, search, registry, presence, changed, risky, sort])

  if (!enabled) return (
    <div class={$layout.container}><p class={$type.muted}>Раздел недоступен.</p></div>
  )

  return (
    <div class={$.page}>

      <div class={clsx($layout.container, $layout.center)}>
        <h3>Контрагенты</h3>

        <div class={$.filters}>
          <form.Field name='search'>
            {(field) => (
              <FormField field={field} label='Поиск по названию или ИНН'>
                <TextInput field={field} />
              </FormField>
            )}
          </form.Field>
          <form.Field name='registry'>
            {(field) => (
              <FormField field={field} label='Реестр'>
                <SelectInput
                  field={field}
                  options={meta.map((m) => ({ value: m.label, label: m.name }))}
                />
              </FormField>
            )}
          </form.Field>
          {registry && (
            <form.Field name='presence'>
              {(field) => (
                <FormField field={field} label='Наличие'>
                  <SelectInput
                    field={field}
                    showEmpty={false}
                    options={[
                      { value: 'has', label: 'есть в реестре' },
                      { value: 'none', label: 'нет в реестре' },
                    ]}
                  />
                </FormField>
              )}
            </form.Field>
          )}
          <form.Field name='changed'>
            {(field) => (
              <FormField field={field} label='Изменения'>
                <SelectInput
                  field={field}
                  options={[
                    { value: '7', label: 'за неделю' },
                    { value: '30', label: 'за месяц' },
                    { value: '90', label: 'за 3 месяца' },
                  ]}
                />
              </FormField>
            )}
          </form.Field>
          <form.Field name='risky'>
            {(field) => (
              <label class={$.riskCheck}>
                <input type='checkbox' checked={field.state.value} onChange={(e) => field.handleChange(e.target.checked)} />
                <span>с рисками</span>
              </label>
            )}
          </form.Field>
        </div>
      </div>

      {filtered.length > 0 ? (
        <div class={$table.scroller}>
          <table class={clsx($table.table, $table.grid, $table.stickyFirstColumn, $table.condensed, $type.small)}>
            <thead>
              <tr>
                <SortHeader label='Контрагент' sortKey='name' sort={sort} onSort={toggleSort} />
                <th>ИНН</th>
                {meta.map((m) => (
                  <SortHeader
                    key={m.label}
                    label={SHORT[m.label] ?? m.label.toUpperCase()}
                    sortKey={`reg:${m.label}`}
                    title={m.name}
                    class='text-center'
                    sort={sort}
                    onSort={toggleSort}
                  />
                ))}
                <th>Риски</th>
                <SortHeader label='Синхронизирован' sortKey='synced' sort={sort} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.inn}
                  class={clsx($.row, r.inn === selected && $.selected, r.inn === syncing && $anim.pulse)}
                  onClick={() => setSelected(r.inn === selected ? null : r.inn)}
                >
                  <td title={r.company_name ?? ''}>{r.company_name ?? '—'}</td>
                  <td>{r.inn}</td>
                  {meta.map((m) => {
                    let found = r.registries[m.label]
                    return (
                      <td key={m.label} class='text-center'>
                        {found == null ? null : found || ''}
                      </td>
                    )
                  })}
                  <td class={$.risks}>
                    <div class={$.riskBadges}>
                      {r.risks.map((x) => (
                        <span key={x.label} class={clsx($badge.pill, $badge.system)}>
                          <span>{SHORT[x.label] ?? x.label.toUpperCase()}</span>
                          <span>{x.status}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{fmt(r.synced_at ?? r.updated_at)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={columnCount} class={$.footerTd}>
                  <div class={clsx($.footer, $type.small, $type.muted)}>
                    <div class={$.syncStatus}>
                      {progress ?
                        <>
                          <Spinner />{' '}
                          {progress.phase === 'freshoffice' ?
                            'Синхронизация данных с FreshOffice…'
                          : `Синхронизация с реестрами…${progress.i != null ? ` (${progress.i}/${progress.total}).` : ''}`}
                        </>
                      : status?.last_sync_at ?
                        `Синхронизировано ${fmt(status.last_sync_at)}.`
                      : null}
                    </div>
                    <div class={$.count}>
                      Показано: {filtered.length}
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <p class={$type.muted} style={{ marginTop: '3em', textAlign: 'center' }}>
          {isFetching ? <Spinner /> : rows.length > 0 ? 'Ничего не найдено.' : 'Нет контрагентов.'}
        </p>
      )}

      {selected && <CounterpartyModal inn={selected} meta={meta} onClose={() => setSelected(null)} />}
    </div>
  )
}
